/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/device/ConnectionDoctor.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)    | Who               | Why                    | What Changed         |
 * |---|----------------------|-------------------|------------------------|----------------------|
 * | 1 | 2026-06-15 (Step 3)  | Claude Sonnet 4.6 | DeviceSession (Step 3) | Created from scratch |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/device/DeviceSession.ts  (classifyError)
 * - apps/studio/src/device/ConnectionDoctor.test.ts  (unit tests)
 * - apps/studio/src/App.tsx  (ConnectionError type, ConnectionErrorKind)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * - ConnectionErrorKind  (union type)
 * - ConnectionError      (interface: kind + message + steps)
 * - classifyError(err)   (pure function — no browser deps, fully testable in Node)
 *
 * CALLER → CALLEE MAP
 * --------------------
 * DeviceSession.connect() → classifyError(caught) → ConnectionError
 * ConnectionDoctorPanel (UI) reads ConnectionError.steps to render remedy list
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. This module has ZERO browser/DOM dependencies. All inputs are plain Error
 *    objects or DOMExceptions (which Node 18+ also provides). Tests run in Node.
 * 2. Error classification follows CLAUDE.md §7 "Windows WebUSB Driver Lottery":
 *    - NetworkError/InvalidStateError → INTERFACE_BUSY  (adb.exe or OEM suite holds interface)
 *    - NotFoundError (not "No device selected") → DRIVER_PROBLEM  (wrong driver binding)
 *    - UNAUTHORIZED in message → UNAUTHORIZED  (phone's "Allow USB debugging?" was denied)
 *    - DeviceBusyError (Tango custom) → INTERFACE_BUSY
 * 3. `steps` are copy-paste-ready, user-facing strings — keep them short and specific.
 * 4. The NO_WEBUSB kind is set by DeviceSession (not from a caught error) when
 *    AdbDaemonWebUsbDeviceManager.BROWSER is undefined.
 *
 * ISSUES FACED / KNOWN GOTCHAS
 * -----------------------------
 * - DOMException.name values differ by browser; normalising to error.name is safest.
 * - DeviceBusyError from Tango is not a DOMException — check constructor.name.
 *
 * =============================================================================
 */

export type ConnectionErrorKind =
  | "NO_WEBUSB"
  | "USER_CANCELLED"
  | "INTERFACE_BUSY"
  | "DRIVER_PROBLEM"
  | "UNAUTHORIZED"
  | "UNKNOWN";

export interface ConnectionError {
  kind: ConnectionErrorKind;
  /** One-sentence summary for display in a status area. */
  message: string;
  /** Ordered, copy-paste-ready remedy steps for the Connection Doctor panel. */
  steps: string[];
}

/**
 * Pure function: classifies any thrown value into a ConnectionError with
 * human-readable remedy steps.  No browser APIs required.
 */
export function classifyError(error: unknown): ConnectionError {
  // ── Sentinel: no WebUSB in this browser / context ──────────────────────
  if (error instanceof Error && error.message === "NO_WEBUSB") {
    return {
      kind: "NO_WEBUSB",
      message: "WebUSB is not available in this browser.",
      steps: [
        "Use Google Chrome 61+ or Microsoft Edge 79+.",
        "Firefox and Safari do not support WebUSB.",
        "In Chrome: check chrome://settings/content/usbDevices — your organisation may have blocked WebUSB via policy.",
        "Alternative: download the Mimiq CLI (Path C) for a local ADB connection.",
      ],
    };
  }

  // ── ADB interface already claimed by adb.exe / Android Studio / OEM ──
  // Tango emits DeviceBusyError; raw WebUSB emits NetworkError / InvalidStateError
  // when claimInterface() fails because another process owns the interface.
  if (
    error instanceof Error &&
    (error.constructor.name === "DeviceBusyError" ||
      error.name === "NetworkError" ||
      error.name === "InvalidStateError" ||
      error.message.includes("Unable to claim interface") ||
      error.message.includes("busy"))
  ) {
    return {
      kind: "INTERFACE_BUSY",
      message:
        "ADB USB interface is held by another process (adb.exe, Android Studio, OEM driver).",
      steps: [
        "Run in a terminal:  adb kill-server",
        "Close Android Studio and any OEM device managers (Samsung DeX, Xiaomi HyperConnect, Huawei HiSuite).",
        "On Windows: open Device Manager → Universal Serial Bus devices → right-click your phone → Update driver → WinUSB.",
        "Set USB mode to 'File Transfer (MTP)' on your phone. 'Charge only' may bind the wrong driver.",
        "If still blocked: try a different USB port, or reboot the phone.",
      ],
    };
  }

  // ── Wrong Windows driver bound to the ADB interface ───────────────────
  // NotFoundError when alternate setting is not found means wrong driver.
  // Distinguish from the user-cancelled case (which also raises NotFoundError).
  if (
    error instanceof Error &&
    error.name === "NotFoundError" &&
    !error.message.includes("No device selected") &&
    !error.message.includes("user gesture")
  ) {
    return {
      kind: "DRIVER_PROBLEM",
      message: "ADB USB interface not accessible — wrong Windows driver may be bound.",
      steps: [
        "Open Device Manager → Universal Serial Bus devices.",
        "Right-click your phone → Update driver → Browse my computer → Let me pick from a list.",
        "Select 'WinUSB Device' (NOT 'ADB Interface' or 'MTP USB Device').",
        "Or use Zadig (zadig.akeo.ie): select your phone → WinUSB → Replace Driver.",
      ],
    };
  }

  // ── User dismissed the device picker without selecting ────────────────
  if (
    error instanceof Error &&
    (error.name === "NotFoundError" ||
      error.message.includes("No device selected") ||
      error.message.includes("user gesture"))
  ) {
    return {
      kind: "USER_CANCELLED",
      message: "Device selection was cancelled.",
      steps: [
        "Click 'Connect' again and select your Android phone from the browser dialog.",
        "Make sure your phone is unlocked and connected via USB.",
      ],
    };
  }

  // ── ADB auth denied on the phone ──────────────────────────────────────
  if (
    error instanceof Error &&
    (error.message.toUpperCase().includes("UNAUTHORIZED") ||
      error.message.includes("AUTH"))
  ) {
    return {
      kind: "UNAUTHORIZED",
      message: "USB debugging not authorised — check your phone screen.",
      steps: [
        "On your phone: tap 'Allow' on the 'Allow USB debugging?' dialog that appeared.",
        "Tick 'Always allow from this computer' to avoid being prompted again.",
        "If no dialog appeared: Settings → Developer options → Revoke USB debugging authorisations, then reconnect.",
      ],
    };
  }

  // ── Catch-all ─────────────────────────────────────────────────────────
  return {
    kind: "UNKNOWN",
    message: error instanceof Error ? error.message : String(error),
    steps: [
      "Check your USB cable and try a different port.",
      "Enable 'USB debugging' in Settings → Developer options on your phone.",
      "Run:  adb kill-server  then reconnect.",
      "Restart your browser and try again.",
    ],
  };
}
